import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Form, message, Tabs } from 'antd';
import dayjs from 'dayjs';
import { Case, CaseStatus, CaseStatistics as CaseStats } from './types';
import {
  getCaseList,
  deleteCase,
  getCaseStatistics,
} from '../../services/case';
import { getStaff, User } from '../../services/user';
import { getUserInfo } from '../../utils/auth';
import { UserRole } from '../../constants/user';
import {
  CaseStatistics,
  CaseFilters,
  CaseListView,
  CaseActions,
} from './components';
import CaseCardView from './components/CaseCardView';
import CaseWizard from './components/CaseWizard';
import CaseDrawer from '../../components/CaseDrawer';
import { useResponsive } from '../../hooks/useResponsive';

const { TabPane } = Tabs;

const CaseManagement: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const [urlParams, setUrlParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [cases, setCases] = useState<Case[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [activeTab, setActiveTab] = useState<'responsible' | 'assistant' | 'all'>('responsible');

  const [searchForm] = Form.useForm();
  const [statistics, setStatistics] = useState<CaseStats | null>(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  
  const currentUser = getUserInfo();
  const isAdmin = currentUser?.role === UserRole.ADMIN;
  
  // 初始化searchParams时就设置默认筛选（我负责的）
  const [searchParams, setSearchParams] = useState<any>(() => {
    return currentUser?.id ? { responsible_lawyer_id: currentUser.id } : {};
  });
  
  const [users, setUsers] = useState<User[]>([]);
  const [wizardVisible, setWizardVisible] = useState(false);
  const [wizardMode, setWizardMode] = useState<'create' | 'edit'>('create');
  const [editingCaseId, setEditingCaseId] = useState<number | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);

  // 初始化时加载统计和用户列表
  useEffect(() => {
    loadStatistics();
    loadUsers();
  }, []);

  // 检查 URL 参数，自动打开详情
  useEffect(() => {
    const openDetailId = urlParams.get('openDetail');
    if (openDetailId) {
      const caseId = parseInt(openDetailId, 10);
      if (!isNaN(caseId)) {
        setSelectedCaseId(caseId);
        setDrawerVisible(true);
        // 清除 URL 参数
        const newParams = new URLSearchParams(urlParams);
        newParams.delete('openDetail');
        setUrlParams(newParams);
      }
    }
  }, [urlParams, setUrlParams]);

  useEffect(() => {
    loadCases();
  }, [page, pageSize, searchParams]);

  const loadCases = async () => {
    setLoading(true);
    try {
      const data = await getCaseList({ 
        page: page, 
        page_size: pageSize, 
        ...searchParams 
      });
      // Cast to our Case type since the API returns the correct structure
      setCases(data.items as any || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to load cases:', error);
      message.error('加载案件列表失败');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    setStatisticsLoading(true);
    try {
      const data = await getCaseStatistics();
      // Cast to our CaseStats type since the API returns the correct structure
      setStatistics(data as any);
    } catch (error) {
      console.error('Failed to load statistics:', error);
    } finally {
      setStatisticsLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      // 使用getStaff API获取所有员工（律师）列表
      const data = await getStaff({ limit: 1000 });
      setUsers(data || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const handleSearch = () => {
    const values = searchForm.getFieldsValue();
    
    // Transform date ranges to API format
    const transformedValues: any = { ...values };
    
    // Handle accept_date_range
    if (values.accept_date_range && values.accept_date_range.length === 2) {
      transformedValues.accept_start_date = values.accept_date_range[0].format('YYYY-MM-DD');
      transformedValues.accept_end_date = values.accept_date_range[1].format('YYYY-MM-DD');
      delete transformedValues.accept_date_range;
    }
    
    // Handle created_date_range
    if (values.created_date_range && values.created_date_range.length === 2) {
      transformedValues.created_start_date = values.created_date_range[0].format('YYYY-MM-DD');
      transformedValues.created_end_date = values.created_date_range[1].format('YYYY-MM-DD');
      delete transformedValues.created_date_range;
    }
    
    // Handle court_date_range
    if (values.court_date_range && values.court_date_range.length === 2) {
      transformedValues.court_start_date = values.court_date_range[0].format('YYYY-MM-DD HH:mm:ss');
      transformedValues.court_end_date = values.court_date_range[1].format('YYYY-MM-DD HH:mm:ss');
      delete transformedValues.court_date_range;
    }
    
    // 根据当前Tab添加对应的筛选条件
    if (activeTab === 'responsible' && currentUser?.id) {
      transformedValues.responsible_lawyer_id = currentUser.id;
    } else if (activeTab === 'assistant' && currentUser?.id) {
      transformedValues.assistant_lawyer_id = currentUser.id;
    }
    
    setSearchParams(transformedValues);
    setPage(1);
  };

  const handleReset = () => {
    searchForm.resetFields();
    
    // 重置时也要保留Tab的筛选条件
    let newParams: any = {};
    if (activeTab === 'responsible' && currentUser?.id) {
      newParams.responsible_lawyer_id = currentUser.id;
    } else if (activeTab === 'assistant' && currentUser?.id) {
      newParams.assistant_lawyer_id = currentUser.id;
    }
    
    setSearchParams(newParams);
    setPage(1);
  };

  const handleCreate = () => {
    // Open the wizard in create mode
    setWizardMode('create');
    setEditingCaseId(null);
    setWizardVisible(true);
  };

  const handleWizardSuccess = (caseId: number) => {
    // Reload data and open the case drawer
    loadCases();
    loadStatistics();
    setWizardVisible(false);
    setEditingCaseId(null);
    setSelectedCaseId(caseId);
    setDrawerVisible(true);
  };

  const handleWizardClose = () => {
    setWizardVisible(false);
    setEditingCaseId(null);
  };

  const handleEdit = (caseItem: Case) => {
    // Open the wizard in edit mode
    setWizardMode('edit');
    setEditingCaseId(caseItem.id);
    setWizardVisible(true);
  };

  const handleView = (caseItem: Case) => {
    setSelectedCaseId(caseItem.id);
    setDrawerVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteCase(id);
      message.success('删除成功');
      loadCases();
      loadStatistics();
    } catch (error) {
      console.error('Failed to delete case:', error);
      message.error('删除失败');
    }
  };



  const handlePageChange = (newPage: number, newPageSize?: number) => {
    setPage(newPage);
    if (newPageSize) setPageSize(newPageSize);
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key as any);
    searchForm.resetFields();
    
    let newParams: any = {};
    if (key === 'responsible' && currentUser?.id) {
      newParams.responsible_lawyer_id = currentUser.id;
    } else if (key === 'assistant' && currentUser?.id) {
      newParams.assistant_lawyer_id = currentUser.id;
    }
    // key === 'all' 时不添加筛选
    
    setSearchParams(newParams);
    setPage(1);
  };

  const handleStatisticsCardClick = (filterType: 'all' | 'responsible' | 'assistant' | 'thisMonth') => {
    switch (filterType) {
      case 'all':
        // 管理员：切换到"全部案件"Tab；普通用户：不做任何操作
        if (isAdmin) {
          setActiveTab('all');
          searchForm.resetFields();
          setSearchParams({});
          setPage(1);
        }
        break;
      case 'responsible':
        // Filter by current user as responsible lawyer
        setActiveTab('responsible');
        searchForm.resetFields();
        if (currentUser?.id) {
          setSearchParams({ responsible_lawyer_id: currentUser.id });
        }
        setPage(1);
        break;
      case 'assistant':
        // Filter by current user as assistant lawyer
        setActiveTab('assistant');
        searchForm.resetFields();
        if (currentUser?.id) {
          setSearchParams({ assistant_lawyer_id: currentUser.id });
        }
        setPage(1);
        break;
      case 'thisMonth':
        // 本月新增：仅作为统计展示，点击不做任何操作
        break;
    }
  };

  return (
    <div className="case-page">
      {/* Statistics */}
      <CaseStatistics 
        statistics={statistics} 
        loading={statisticsLoading}
        onCardClick={handleStatisticsCardClick}
        isAdmin={isAdmin}
      />

      {/* Main Content */}
      <Card className="case-list-view">
        {/* Filters */}
        <CaseFilters 
          form={searchForm} 
          onSearch={handleSearch} 
          onReset={handleReset} 
          loading={loading}
          users={users}
          activeTab={activeTab}
        />

        {/* Actions */}
        <CaseActions 
          onCreateCase={handleCreate} 
          onSearch={handleSearch}
          onReset={handleReset}
          loading={loading}
        />

        {/* Tab切换 */}
        <Tabs activeKey={activeTab} onChange={handleTabChange} style={{ marginBottom: 16 }}>
          <TabPane tab="我负责的" key="responsible" />
          <TabPane tab="我协助的" key="assistant" />
          {isAdmin && <TabPane tab="全部案件" key="all" />}
        </Tabs>

        {/* List */}
        {isMobile ? (
          <CaseCardView
            cases={cases}
            loading={loading}
            onView={handleView}
            onEdit={handleEdit}
          />
        ) : (
          <CaseListView
            cases={cases}
            loading={loading}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: total,
              onChange: handlePageChange,
            }}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onRefresh={() => {
              loadCases();
              loadStatistics();
            }}
          />
        )}
      </Card>

      {/* Case Wizard */}
      <CaseWizard
        visible={wizardVisible}
        mode={wizardMode}
        caseId={editingCaseId || undefined}
        onClose={handleWizardClose}
        onSuccess={handleWizardSuccess}
      />

      {/* Case Drawer */}
      <CaseDrawer
        caseId={selectedCaseId}
        visible={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedCaseId(null);
        }}
        onUpdate={() => {
          loadCases();
          loadStatistics();
        }}
        onDelete={() => {
          loadCases();
          loadStatistics();
        }}
      />

    </div>
  );
};

export default CaseManagement;
